import { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";
import { fetchCurrencies, fetchData } from "../utilities/api";

export const useCurrency = () => {
	const queryClient = useQueryClient();

	// Retrieve initial currency from localStorage or default to "USD"
	const getCurrency = () => {
		return localStorage.getItem("currency")
			? JSON.parse(localStorage.getItem("currency"))
			: { name: "USD", value: 1 };
	};

	// Function to update currency both in localStorage and React Query
	const setCurrency = (currency) => {
		localStorage.setItem("currency", JSON.stringify(currency)); // Save to localStorage
		queryClient.setQueryData(["currency"], currency); // Update React Query cache
	};

	// Use React Query to store currency
	const { data: currency } = useQuery(["currency"], getCurrency, {});

	return {
		setCurrency,
		currency,
	};
};

const CurrencySelectorDropdown = () => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);
	const { data, isLoading, isError, error } = useQuery("fetchData", fetchData);
	const { currency, setCurrency } = useCurrency();

	const currencies = useMemo(() => {
		if (!data?.currenciesCache) return [];
		const currencies = Object.keys(data?.currenciesCache);
		return currencies.map((currency) => ({
			name: currency,
			value: data.currenciesCache[currency],
		}));
	}, [data]);

	const toggleDropdown = () => {
		setIsOpen(!isOpen);
	};

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener("click", handleClickOutside);
		return () => document.removeEventListener("click", handleClickOutside);
	}, []);

	return (
		<div className="relative inline-block text-left" ref={dropdownRef}>
			{/* Dropdown Button */}
			<button
				onClick={toggleDropdown}
				className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none"
			>
				{currency?.name} ▼
			</button>

			{/* Dropdown Menu */}
			<div
				className={`absolute max-h-[500px] overflow-y-auto right-0 mt-2 w-20 bg-white border border-gray-300 shadow-lg rounded-md transition-all duration-300 ${
					isOpen
						? "opacity-100 visible scale-100"
						: "opacity-0 invisible scale-95"
				}`}
			>
				<ul className="py-2">
					{currencies?.map((item) => (
						<li
							onClick={() => setCurrency(item)}
							key={item.name}
							className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
						>
							{item.name}
						</li>
					))}
				</ul>
			</div>
		</div>
	);
};
// Currencies most likely to be picked, pinned above the full alphabetical list.
const PINNED_CURRENCIES = ["USD", "CHF", "EUR", "GBP", "INR"];

// A native select: keyboard type-ahead over ~160 codes, and it works on mobile.
export const CurrencySelectorDropdownNew = ({ className } = {}) => {
	const { data } = useQuery("fetchCurrencies", fetchCurrencies);
	const { currency, setCurrency } = useCurrency();

	const { pinned, rest } = useMemo(() => {
		const names = Object.keys(data || {}).sort();
		return {
			pinned: PINNED_CURRENCIES.filter((name) => names.includes(name)),
			rest: names.filter((name) => !PINNED_CURRENCIES.includes(name)),
		};
	}, [data]);

	const selected = currency?.name || "USD";
	const options = pinned.length || rest.length ? null : [selected];

	return (
		<label className={`relative inline-flex ${className || ""}`}>
			<span className="sr-only">Display currency</span>
			<select
				value={selected}
				onChange={(event) => {
					const name = event.target.value;
					if (data?.[name]) setCurrency({ name, value: data[name] });
				}}
				className="h-9 cursor-pointer appearance-none rounded-md border border-slate-200 bg-white pl-3 pr-8 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
			>
				{options?.map((name) => (
					<option key={name} value={name}>
						{name}
					</option>
				))}
				{pinned.length > 0 && (
					<optgroup label="Common">
						{pinned.map((name) => (
							<option key={name} value={name}>
								{name}
							</option>
						))}
					</optgroup>
				)}
				{rest.length > 0 && (
					<optgroup label="All currencies">
						{rest.map((name) => (
							<option key={name} value={name}>
								{name}
							</option>
						))}
					</optgroup>
				)}
			</select>
			<svg
				aria-hidden="true"
				viewBox="0 0 20 20"
				className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
			>
				<path fill="currentColor" d="M5.3 7.3a1 1 0 0 1 1.4 0L10 10.6l3.3-3.3a1 1 0 1 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4Z" />
			</svg>
		</label>
	);
};

export default CurrencySelectorDropdown;
